/**
 * Firm-side Checklists page.
 *
 * Lives at `/checklists`. Lets the firm:
 *   - See every active checklist with progress bars
 *   - Create a checklist from scratch or from a built-in template
 *   - Drill into a checklist to review uploads & send a reminder
 *
 * Most of the surface area is `<ChecklistDetail />` (the right pane) — it
 * stays mounted as the user clicks between checklists so the state
 * transitions feel snappy.
 */
import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../fetchInterceptor';
import { useWorkspace } from '../context/WorkspaceContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ClipboardList,
  Plus,
  Send,
  CheckCircle2,
  AlertCircle,
  Clock,
  Trash2,
  Sparkles,
  FileCheck2,
  Eye,
  Download,
  Pencil,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  needed: { label: 'Needed', cls: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30', icon: AlertCircle },
  uploaded: { label: 'Uploaded', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30', icon: FileCheck2 },
  in_review: { label: 'In Review', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30', icon: Clock },
  approved: { label: 'Approved', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', icon: CheckCircle2 },
  rejected: { label: 'Rejected', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30', icon: AlertCircle },
};

export default function Checklists() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/checklists`);
      if (res.ok) {
        const data = await res.json();
        const rows = Array.isArray(data.checklists) ? data.checklists : [];
        setChecklists(rows);
        if (rows.length && !selectedId) setSelectedId(rows[0].checklist_id);
      }
    } catch (e) {
      toast.error('Failed to load checklists');
    } finally {
      setLoading(false);
    }
  }, [wsId, selectedId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="checklists-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-emerald-400" />
            Document Checklists
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Send clients a checklist of documents you need. They upload directly into each slot.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="create-checklist-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New Checklist
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
        {/* Left: list */}
        <Card className="h-fit">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-300">
              All Checklists ({checklists.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : checklists.length === 0 ? (
              <div className="text-center py-12 px-4 text-zinc-500">
                <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No checklists yet.</p>
                <p className="text-xs mt-1">Create your first one →</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {checklists.map(c => {
                  const pct = c.progress?.total
                    ? Math.round((c.progress.completed / c.progress.total) * 100)
                    : 0;
                  const isSelected = selectedId === c.checklist_id;
                  return (
                    <button
                      key={c.checklist_id}
                      onClick={() => setSelectedId(c.checklist_id)}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        isSelected ? 'bg-emerald-500/10' : 'hover:bg-white/[0.03]'
                      }`}
                      data-testid={`checklist-row-${c.checklist_id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-zinc-100 truncate">{c.title}</p>
                        {c.status === 'submitted' && (
                          <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px]">
                            Submitted
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 truncate mb-2">{c.contact_name}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-[11px] text-zinc-400 font-medium">
                          {c.progress?.completed || 0}/{c.progress?.total || 0}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: detail */}
        <div>
          {selectedId ? (
            <ChecklistDetail
              key={selectedId}
              workspaceId={wsId}
              checklistId={selectedId}
              onChanged={fetchAll}
              onDeleted={() => { setSelectedId(null); fetchAll(); }}
            />
          ) : (
            <Card className="h-full">
              <CardContent className="flex flex-col items-center justify-center py-20 text-zinc-500">
                <ClipboardList className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-sm">Select a checklist to review.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <CreateChecklistDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        workspaceId={wsId}
        onCreated={(id) => {
          setSelectedId(id);
          fetchAll();
        }}
      />
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Create dialog (with template picker)
// ──────────────────────────────────────────────────────────────────────

function CreateChecklistDialog({ open, onOpenChange, workspaceId, onCreated }) {
  const [tab, setTab] = useState('template'); // template | blank
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState('');
  const [templateKey, setTemplateKey] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !workspaceId) return;
    (async () => {
      const [tRes, cRes] = await Promise.all([
        apiFetch(`${API_URL}/api/workspaces/${workspaceId}/checklists/templates`),
        apiFetch(`${API_URL}/api/workspaces/${workspaceId}/contacts?contact_type=customer`),
      ]);
      if (tRes.ok) {
        const td = await tRes.json();
        setTemplates(Array.isArray(td.templates) ? td.templates : []);
      }
      if (cRes.ok) {
        const cd = await cRes.json();
        // contacts endpoint sometimes returns {contacts: [...]}, sometimes array
        const list = Array.isArray(cd) ? cd : (cd.contacts || []);
        setContacts(list.filter(c => c.contact_type === 'customer' || !c.contact_type));
      }
    })();
  }, [open, workspaceId]);

  const handleSubmit = async () => {
    if (!contactId) return toast.error('Pick a client');
    setSubmitting(true);
    try {
      let res;
      if (tab === 'template') {
        if (!templateKey) {
          setSubmitting(false);
          return toast.error('Pick a template');
        }
        res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/checklists/from-template`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contact_id: contactId, template_key: templateKey }),
        });
      } else {
        if (!title.trim()) {
          setSubmitting(false);
          return toast.error('Title is required');
        }
        res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/checklists`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_id: contactId,
            title: title.trim(),
            description: description.trim(),
            items: [],
          }),
        });
      }
      if (!res.ok) throw new Error('create failed');
      const data = await res.json();
      toast.success('Checklist created');
      onCreated(data.checklist_id);
      onOpenChange(false);
      setContactId(''); setTemplateKey(''); setTitle(''); setDescription('');
    } catch (e) {
      toast.error('Could not create');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="create-checklist-dialog">
        <DialogHeader>
          <DialogTitle>New Checklist</DialogTitle>
          <DialogDescription>Pick a template or start blank.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs text-zinc-400">Client</Label>
            <Select value={contactId} onValueChange={setContactId}>
              <SelectTrigger data-testid="create-checklist-contact">
                <SelectValue placeholder="Pick a client…" />
              </SelectTrigger>
              <SelectContent>
                {contacts.map(c => (
                  <SelectItem key={c.contact_id} value={c.contact_id}>
                    {c.name} {c.email ? `· ${c.email}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-white/10">
            <button
              onClick={() => setTab('template')}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === 'template' ? 'border-emerald-500 text-zinc-100' : 'border-transparent text-zinc-500'}`}
              data-testid="create-checklist-tab-template"
            >
              <Sparkles className="w-3.5 h-3.5 inline mr-1" /> From Template
            </button>
            <button
              onClick={() => setTab('blank')}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === 'blank' ? 'border-emerald-500 text-zinc-100' : 'border-transparent text-zinc-500'}`}
              data-testid="create-checklist-tab-blank"
            >
              <Plus className="w-3.5 h-3.5 inline mr-1" /> Blank
            </button>
          </div>

          {tab === 'template' ? (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {templates.map(t => (
                <button
                  key={t.template_key}
                  onClick={() => setTemplateKey(t.template_key)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    templateKey === t.template_key
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-white/10 hover:border-white/20 hover:bg-white/[0.02]'
                  }`}
                  data-testid={`template-${t.template_key}`}
                >
                  <p className="text-sm font-medium text-zinc-100">{t.title}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{t.description}</p>
                  <p className="text-[11px] text-zinc-600 mt-1">{t.items.length} items</p>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-zinc-400">Title</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. 2024 Tax Return Documents"
                  data-testid="create-checklist-title"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Description (optional)</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Any context you want the client to see"
                />
              </div>
              <p className="text-xs text-zinc-500">
                You'll add items after creating the checklist.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="create-checklist-submit"
            >
              {submitting ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Detail panel
// ──────────────────────────────────────────────────────────────────────

function ChecklistDetail({ workspaceId, checklistId, onChanged, onDeleted }) {
  const [chk, setChk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingItem, setAddingItem] = useState(false);
  const [newItemLabel, setNewItemLabel] = useState('');
  const [newItemDesc, setNewItemDesc] = useState('');
  const [newItemRequired, setNewItemRequired] = useState(true);
  const [reminding, setReminding] = useState(false);

  const fetchOne = useCallback(async () => {
    if (!workspaceId || !checklistId) return;
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/checklists/${checklistId}`);
      if (res.ok) setChk(await res.json());
    } finally {
      setLoading(false);
    }
  }, [workspaceId, checklistId]);

  useEffect(() => { fetchOne(); }, [fetchOne]);

  const addItem = async () => {
    if (!newItemLabel.trim()) return;
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/checklists/${checklistId}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: newItemLabel.trim(),
          description: newItemDesc.trim(),
          required: newItemRequired,
        }),
      });
      if (!res.ok) throw new Error();
      setNewItemLabel(''); setNewItemDesc(''); setNewItemRequired(true); setAddingItem(false);
      fetchOne();
      onChanged && onChanged();
    } catch {
      toast.error('Could not add item');
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    const res = await apiFetch(
      `${API_URL}/api/workspaces/${workspaceId}/checklists/${checklistId}/items/${itemId}`,
      { method: 'DELETE' },
    );
    if (res.ok) { fetchOne(); onChanged && onChanged(); }
  };

  const reviewItem = async (itemId, action) => {
    const note = action === 'reject' ? window.prompt('Note for the client (optional):') : null;
    const res = await apiFetch(
      `${API_URL}/api/workspaces/${workspaceId}/checklists/${checklistId}/items/${itemId}/review`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, note }),
      },
    );
    if (res.ok) { fetchOne(); onChanged && onChanged(); toast.success(action === 'approve' ? 'Approved' : 'Marked rejected'); }
  };

  const sendReminder = async () => {
    setReminding(true);
    try {
      const res = await apiFetch(
        `${API_URL}/api/workspaces/${workspaceId}/checklists/${checklistId}/send-reminder`,
        { method: 'POST' },
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(`Reminder sent to ${data.sent_to}`);
        fetchOne();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Could not send reminder');
      }
    } finally { setReminding(false); }
  };

  const deleteChecklist = async () => {
    if (!window.confirm('Delete this entire checklist? This cannot be undone.')) return;
    const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/checklists/${checklistId}`, {
      method: 'DELETE',
    });
    if (res.ok) { toast.success('Deleted'); onDeleted && onDeleted(); }
  };

  const downloadDoc = async (documentId) => {
    const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/client-documents/${documentId}/download`);
    if (!res.ok) return toast.error('Could not download');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  if (loading || !chk) {
    return (
      <Card>
        <CardContent className="p-6 space-y-3">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  const prog = chk.progress || { completed: 0, total: 0, required_completed: 0, required_total: 0 };

  return (
    <Card data-testid="checklist-detail">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-zinc-100">{chk.title}</h2>
            <p className="text-sm text-zinc-400 mt-0.5">
              For <strong className="text-zinc-200">{chk.contact_name}</strong>
              {chk.contact_email && <span className="text-zinc-500"> · {chk.contact_email}</span>}
            </p>
            {chk.description && <p className="text-sm text-zinc-500 mt-2">{chk.description}</p>}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={sendReminder}
              disabled={reminding || !chk.contact_email}
              data-testid="send-reminder-btn"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {reminding ? 'Sending…' : 'Send Reminder'}
            </Button>
            <Button size="sm" variant="ghost" onClick={deleteChecklist} className="text-rose-400 hover:text-rose-300" data-testid="delete-checklist-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-3 flex items-center gap-3">
          <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all"
              style={{ width: `${prog.total ? (prog.completed / prog.total) * 100 : 0}%` }}
            />
          </div>
          <span className="text-xs text-zinc-400 font-medium" data-testid="checklist-progress">
            {prog.completed}/{prog.total} items · {prog.required_completed}/{prog.required_total} required
          </span>
        </div>
        {chk.last_reminder_sent_at && (
          <p className="text-[11px] text-zinc-500 mt-1">
            Last reminder sent {new Date(chk.last_reminder_sent_at).toLocaleString()}
          </p>
        )}
        {chk.submitted_at && (
          <Badge className="mt-2 bg-emerald-500/20 text-emerald-300 border-emerald-500/30 w-fit">
            Client submitted on {new Date(chk.submitted_at).toLocaleDateString()}
          </Badge>
        )}
      </CardHeader>

      <CardContent className="space-y-2">
        {(chk.items || []).map(item => {
          const meta = STATUS_META[item.status] || STATUS_META.needed;
          const Icon = meta.icon;
          return (
            <div
              key={item.item_id}
              className="border border-white/5 rounded-lg p-3 bg-white/[0.02]"
              data-testid={`item-row-${item.item_id}`}
            >
              <div className="flex items-start gap-3">
                <Icon className="w-4 h-4 mt-0.5 text-zinc-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-zinc-100">{item.label}</p>
                    {item.required && (
                      <Badge variant="outline" className="text-[10px] bg-rose-500/10 text-rose-400 border-rose-500/30">
                        Required
                      </Badge>
                    )}
                    <Badge variant="outline" className={`text-[10px] ${meta.cls}`}>{meta.label}</Badge>
                  </div>
                  {item.description && <p className="text-xs text-zinc-500 mt-1">{item.description}</p>}
                  {item.upload_filename && (
                    <button
                      onClick={() => downloadDoc(item.upload_document_id)}
                      className="text-xs text-sky-400 hover:text-sky-300 mt-2 flex items-center gap-1"
                      data-testid={`download-upload-${item.item_id}`}
                    >
                      <Download className="w-3 h-3" /> {item.upload_filename}
                    </button>
                  )}
                  {item.review_note && (
                    <p className="text-xs text-zinc-400 mt-1 italic">Note: {item.review_note}</p>
                  )}
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  {item.status === 'in_review' && (
                    <>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => reviewItem(item.item_id, 'approve')}
                        className="h-7 px-2 text-xs text-emerald-400 hover:text-emerald-300"
                        data-testid={`approve-${item.item_id}`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => reviewItem(item.item_id, 'reject')}
                        className="h-7 px-2 text-xs text-rose-400 hover:text-rose-300"
                        data-testid={`reject-${item.item_id}`}
                      >
                        <AlertCircle className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => deleteItem(item.item_id)}
                    className="h-7 px-2 text-xs text-zinc-500 hover:text-rose-400"
                    data-testid={`delete-item-${item.item_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}

        {/* Add item */}
        {addingItem ? (
          <div className="border border-emerald-500/30 rounded-lg p-3 space-y-2 bg-emerald-500/5">
            <Input
              autoFocus
              value={newItemLabel}
              onChange={(e) => setNewItemLabel(e.target.value)}
              placeholder="Item label (e.g. W-2)"
              data-testid="new-item-label"
            />
            <Input
              value={newItemDesc}
              onChange={(e) => setNewItemDesc(e.target.value)}
              placeholder="Description (optional)"
            />
            <label className="flex items-center gap-2 text-xs text-zinc-400">
              <input
                type="checkbox"
                checked={newItemRequired}
                onChange={(e) => setNewItemRequired(e.target.checked)}
                className="rounded"
              />
              Required
            </label>
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={addItem} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-item-confirm">
                Add Item
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setAddingItem(false); setNewItemLabel(''); }}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAddingItem(true)}
            className="w-full border-dashed"
            data-testid="add-item-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add Item
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
